// src/App.js
import { lazy, Suspense, useCallback, useEffect, useState } from "react";
import { useAuth } from "./context/AuthContext";
import { C, F, MAXW } from "./constants";
import { Icon, TopBar, BottomNav, SideNav, FloatPts, Toast, Spinner, OfflineBar } from "./components/UI";
import { PartnerStrip } from "./components/PartnerLockup";
import { useBreakpoint } from "./useResponsive";
import { AuthScreen, RoleScreen } from "./screens/AuthFlow";
import HomeTabs from "./screens/HomeTabs";
import { ehStore } from "./eh/store";
import { isOnline, syncAll, watchConnectivity } from "./eh/sync";
import { DEMO_MODE } from "./eh/demoMode";

// Route-level code splitting. Nothing below is on the first screen a volunteer
// sees, and between them they pull in the entire environmental-health data layer
// and charts, the Leaflet map bindings and the Firestore SDK. Splitting them
// keeps the initial download — the one that has to finish over a weak connection
// before anything at all works — down to the shell and the home screen.
const EHShell = lazy(() => import("./screens/eh/EHShell"));
const AdminPanel = lazy(() => import("./screens/AdminPanel"));
const NGODashboard = lazy(() => import("./screens/NGODashboard"));
const MissionBoard = lazy(() => import("./screens/MissionBoard"));
const ReportTab = lazy(() => import("./screens/ReportTab"));
const PitchPage = lazy(() => import("./screens/PitchPage"));
const MapTab = lazy(() => import("./screens/OtherTabs").then((m) => ({ default: m.MapTab })));
const LearnTab = lazy(() => import("./screens/OtherTabs").then((m) => ({ default: m.LearnTab })));
const ProfileTab = lazy(() => import("./screens/OtherTabs").then((m) => ({ default: m.ProfileTab })));
// Heights of the two fixed strips above the scroller. These were a literal 57
// repeated in three places — the notifications overlay's offset, the overlay's
// height, and the scroll container's height — which is exactly the kind of
// number that goes stale the moment anything is added to the header. Naming
// them means the partner bar below could be added by changing one sum.
//
// PARTNER_BAR_H is asserted by the bar itself, which sets this as an explicit
// height rather than letting its content decide: if the label ever wrapped, a
// content-sized bar would silently push the scroller off the bottom of the
// viewport.
const TOPBAR_H = 57;
const PARTNER_BAR_H = 45;

const NotificationsPanel = lazy(() =>
  import("./screens/OtherTabs").then((m) => ({ default: m.NotificationsPanel }))
);

// ─── Loading splash ───────────────────────────────────────────────────────────
function LoadingScreen({ message = "Restoring your session…" }) {
  return (
    <div
      style={{
        minHeight: "100vh",
        background: C.bg,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 20,
        fontFamily: F,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <Icon name="water_drop" size={32} color={C.primary} />
        <span style={{ fontSize: 32, fontWeight: 800, color: C.text, letterSpacing: "-1px" }}>
          Coast<span style={{ color: C.primary }}>Clear</span>
        </span>
      </div>
      <Spinner size={32} color={C.primary} />
      <span style={{ fontSize: 12, color: C.muted }}>{message}</span>
    </div>
  );
}

/** Placeholder while a split chunk downloads. */
function ChunkFallback({ label }) {
  return (
    <div
      style={{
        padding: "60px 20px",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 14,
        fontFamily: F,
      }}
    >
      <Spinner size={26} color={C.primary} label={label} />
      <span style={{ fontSize: 12.5, color: C.muted }}>{label}</span>
    </div>
  );
}

/**
 * Connection and queue state for the whole app.
 *
 * The environmental-health module has always shown this on its own screens, but
 * a volunteer filling in a waste report or a survey on the Idenau road had no
 * indication anywhere else that they were offline or that anything was waiting
 * to upload. Since capture always writes locally first, "offline" is a normal
 * working state rather than an error — but it has to be visible, and it has to
 * distinguish saved-and-queued from saved-and-sent.
 */
/**
 * Seeds the sample dataset once, at app level.
 *
 * It used to live in EHShell, which only mounts after sign-in — but the landing
 * page shows metrics computed from this data to a signed-out visitor, so it has
 * to happen earlier. Imported dynamically so the EH data layer stays out of the
 * entry chunk; seedDemoDataOnce is itself idempotent and latched.
 */
function useDemoSeed() {
  useEffect(() => {
    if (!DEMO_MODE) return;
    let alive = true;
    (async () => {
      try {
        const [{ ehStore }, { seedDemoDataOnce }] = await Promise.all([
          import("./eh/store"),
          import("./eh/demoData"),
        ]);
        await ehStore.ready();
        if (alive) await seedDemoDataOnce();
      } catch (err) {
        console.error("Demo seed could not run:", err);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);
}

function useConnection() {
  const [online, setOnline] = useState(isOnline);
  const [queued, setQueued] = useState(0);
  const [syncing, setSyncing] = useState(false);

  useEffect(() => {
    let alive = true;
    const refresh = () => {
      if (!alive) return;
      const { pending, error } = ehStore.syncStats();
      setQueued(pending + error);
    };

    ehStore.ready().then(refresh);
    const unsubStore = ehStore.subscribe(refresh);
    const unwatch = watchConnectivity((next) => {
      if (alive) setOnline(next);
    });

    return () => {
      alive = false;
      unsubStore();
      unwatch();
    };
  }, []);

  const retry = useCallback(async () => {
    if (syncing) return;
    setSyncing(true);
    try {
      await syncAll();
    } finally {
      setSyncing(false);
    }
  }, [syncing]);

  return { online, queued, retry };
}

// ─── Main AppShell ────────────────────────────────────────────────────────────
function AppShell() {
  const { profile, lang, unreadCount } = useAuth();
  const { isDesktop, isTablet } = useBreakpoint();
  const { online, queued, retry } = useConnection();

  const [tab, setTab] = useState("home");
  const [floatPts, setFloatPts] = useState(null);
  const [toast, setToast] = useState(null);
  const [showNotifs, setShowNotifs] = useState(false);
  const [showAdmin, setShowAdmin] = useState(false);

  const earn = (n, msg) => {
    setFloatPts(n);
    if (msg) setToast(msg);
  };

  const roleColor =
    profile?.role === "cleanup"
      ? C.green
      : profile?.role === "ngo"
      ? C.gold
      : C.primary;

  if (showAdmin && profile?.isAdmin) {
    return (
      <Suspense fallback={<LoadingScreen message="Opening the admin panel…" />}>
        <AdminPanel onClose={() => setShowAdmin(false)} />
      </Suspense>
    );
  }

  if (profile?.role === "ngo") {
    return (
      <Suspense fallback={<LoadingScreen message="Opening the coordinator panel…" />}>
        <NGODashboard />
      </Suspense>
    );
  }

  // The column width the content sits in. On a phone this is the whole
  // viewport; on a desktop the side rail takes its own space and the content
  // gets the rest, capped so a dashboard does not stretch into unreadable
  // line lengths on a very wide monitor.
  //
  // The cap is only half the job. It used to be paired with a left-aligned
  // margin, so past about 1400px the column stopped growing but stayed pinned
  // against the rail and every extra pixel piled up as one dead band on the
  // right — the wider the monitor, the more the page looked abandoned rather
  // than laid out. `margin: 0 auto` spends that slack evenly on both sides, so
  // the content reads as centred in the space beside the rail. Screens that
  // want the width rather than the whitespace ask for it themselves, by
  // reflowing into columns at the desktop breakpoint (see `.cc-duo`).
  const columnWidth = isDesktop ? MAXW.wide : isTablet ? MAXW.tablet : MAXW.mobile;

  // Everything fixed above the scroller. The partner bar is only in the sum
  // where it is actually rendered — on desktop the side rail carries the marks
  // instead, so the header is the top bar alone.
  const headerH = TOPBAR_H + (isDesktop ? 0 : PARTNER_BAR_H);

  return (
    <div
      style={{
        display: isDesktop ? "flex" : "block",
        alignItems: "flex-start",
        minHeight: "100vh",
        background: C.bg,
        fontFamily: F,
      }}
    >
      {floatPts && <FloatPts pts={floatPts} onDone={() => setFloatPts(null)} />}
      {toast && <Toast msg={toast} onDone={() => setToast(null)} />}

      {isDesktop && (
        <SideNav tab={tab} setTab={setTab} role={profile?.role || "reporter"} lang={lang} />
      )}

      <div
        style={{
          flex: 1,
          minWidth: 0,
          maxWidth: columnWidth,
          margin: "0 auto",
          width: "100%",
          minHeight: "100vh",
          background: C.bg,
          position: "relative",
          overflow: "hidden",
        }}
      >
      <TopBar
        profile={profile}
        unreadCount={unreadCount}
        onBellClick={() => {
          setShowNotifs((v) => !v);
          setTab("home");
        }}
        roleColor={roleColor}
      />

      {/* Partner bar. It sits outside the scroll container, so it stays in
          view on every screen rather than scrolling away with the content —
          the association is meant to be constant, not something you catch at
          the top of a page.

          Desktop does not get it here: the side rail carries the two marks
          under the CoastClear wordmark instead, where there is room to show
          them larger, and repeating them across the top as well would just be
          the same logos twice on one screen. */}
      {!isDesktop && (
        <div
          style={{
            height: PARTNER_BAR_H,
            flexShrink: 0,
            background: C.card,
            borderBottom: `1px solid ${C.border}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "0 16px",
            overflow: "hidden",
          }}
        >
          <PartnerStrip lang={lang} base={30} labelClassName="cc-partner-bar-label" />
        </div>
      )}

      <OfflineBar online={online} queued={queued} onRetry={retry} />

      {/* Notifications overlay.

          Absolute inside the content column rather than fixed to the viewport.
          Fixed positioning meant the panel had to re-derive the column's
          position from the viewport with its own width and centring maths — a
          second copy of the layout that was already wrong wherever the column
          was narrower than its cap, and that would have drifted again now the
          column centres itself. Anchoring it to the column removes the
          duplicate: `left: 0, right: 0` is the column's width by definition,
          whatever the viewport is doing. The height matches the scroller it
          covers, so the panel stops where the content does. */}
      {showNotifs && (
        <div
          style={{
            position: "absolute",
            top: headerH,
            left: 0,
            right: 0,
            height: `calc(100vh - ${headerH}px)`,
            zIndex: 400,
          }}
        >
          <Suspense fallback={<ChunkFallback label="Loading notifications…" />}>
            <NotificationsPanel onClose={() => setShowNotifs(false)} lang={lang} />
          </Suspense>
        </div>
      )}

      {/* Main tab content */}
      {!showNotifs && (
        <div
          style={{
            height: `calc(100vh - ${headerH}px)`,
            overflowY: "auto",
            scrollbarWidth: "none",
            paddingBottom: isDesktop ? 32 : 80,
          }}
        >
          {tab === "home" && <HomeTabs setTab={setTab} onEarn={earn} lang={lang} />}

          {tab === "report" && (
            <Suspense fallback={<ChunkFallback label="Loading the report form…" />}>
              <ReportTab
                onSubmit={(n, msg) => {
                  earn(n, msg);
                  setTab("home");
                }}
                role={profile?.role}
                lang={lang}
              />
            </Suspense>
          )}

          {/* Environmental-health module — surveillance, risk, response, M&E */}
          {tab === "eh" && (
            <Suspense fallback={<ChunkFallback label="Loading environmental health…" />}>
              <EHShell lang={lang} />
            </Suspense>
          )}

          {tab === "map" && (
            <Suspense fallback={<ChunkFallback label="Loading the map…" />}>
              <MapTab lang={lang} />
            </Suspense>
          )}

          {tab === "learn" && (
            <Suspense fallback={<ChunkFallback label="Loading articles…" />}>
              <LearnTab lang={lang} />
            </Suspense>
          )}

          {tab === "missions" && (
            <Suspense fallback={<ChunkFallback label="Loading missions…" />}>
              <MissionBoard />
            </Suspense>
          )}

          {tab === "profile" && (
            <Suspense fallback={<ChunkFallback label="Loading your profile…" />}>
              <ProfileTab
                lang={lang}
                onEarn={earn}
                onOpenAdmin={() => setShowAdmin(true)}
              />
            </Suspense>
          )}
        </div>
      )}

      {!showNotifs && !isDesktop && (
        <BottomNav
          tab={tab}
          setTab={setTab}
          role={profile?.role || "reporter"}
          lang={lang}
          maxWidth={columnWidth}
        />
      )}
      </div>
    </div>
  );
}

// ─── Root App ─────────────────────────────────────────────────────────────────
export default function App() {
  const { user, profile, loading, lang, setLang } = useAuth();

  // pitch | register | signin. A returning signed-in user skips all of it.
  const [gate, setGate] = useState("pitch");

  useDemoSeed();

  if (loading) return <LoadingScreen />;

  if (user && profile && !profile.role) {
    return (
      <div style={{ maxWidth: 480, margin: "0 auto", fontFamily: F }}>
        <RoleScreen lang={lang} />
      </div>
    );
  }

  if (user && profile?.role) {
    return <AppShell />;
  }

  if (gate === "pitch") {
    return (
      <Suspense fallback={<LoadingScreen message="Loading…" />}>
        <PitchPage
          lang={lang}
          setLang={setLang}
          onCreateAccount={() => setGate("register")}
          onSignIn={() => setGate("signin")}
        />
      </Suspense>
    );
  }

  return (
    <div style={{ maxWidth: 480, margin: "0 auto", fontFamily: F }}>
      <AuthScreen
        lang={lang}
        initialView={gate === "register" ? "reg" : "log"}
        onBackToPitch={() => setGate("pitch")}
        onSuccess={() => {}}
      />
    </div>
  );
}
